import { Fragment, useEffect, useMemo, useState } from "react";
import type {
  CommentStatus,
  CommentType,
  VisibilityChange,
  WorkspaceMember,
} from "../../api/types";
import type { ResourceRole, WorkbenchComment } from "../../api/articleUnits";
import type { VersionTickLike } from "../../lib/commentVersionState";
import { ArrowRight, MessagesSquare } from "../../compose/Icon";
import { EmptyState } from "../../compose/states";
import type { AnchorState } from "../../lib/anchoring";
import type { DocumentOrder } from "../../lib/reviewOrder";
import {
  loadReviewSort,
  type ReviewSort,
  saveReviewSort,
  sortRoots,
  unanchoredRoots,
} from "../../lib/reviewSort";
import { buildThreads } from "../../lib/threads";
import { commentBelongsInGutter, counterpartLabel } from "../../lib/workbenchLayout";
import { CommentThread, type ThreadCallbacks } from "../review/CommentThread";
import { ReviewNotesSort } from "../review/ReviewNotesSort";
import { NO_UNANCHORED, UnanchoredHeading } from "../review/UnanchoredHeading";

/**
 * `<CommentGutter>` — today's `ReviewRail` generalized (design §2.5): the same
 * `buildThreads` + `<CommentThread>` + type/status filter pills, but bound to a
 * PAIRING (`brief-article` / `article-research` / `brief-research` / `solo:<k>`)
 * and its two side panes. The pure §2.5 placement fn (`commentBelongsInGutter`)
 * decides which comments land here — no comment is ever double-counted, and
 * `pairing_side` disambiguates article-homed notes. Each root shows a counterpart
 * chip ("↔ Brief" / "↔ Research") that scrolls the OTHER pane to the linked
 * section. (W1 = read-only-ish rails; full dual-gutter authoring is Wave 3.)
 */

type TypeFilter = "all" | CommentType;
type StatusFilter = "all" | CommentStatus;

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All types" },
  { value: "comment", label: "Comment" },
  { value: "suggestion", label: "Suggestion" },
  { value: "request-for-change", label: "Change" },
];

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "acted_on", label: "Acted-on" },
  { value: "pinned", label: "Pinned" },
];

export interface CommentGutterProps {
  readonly pairing: string;
  readonly comments: WorkbenchComment[];
  readonly members: WorkspaceMember[];
  readonly anchorStates: Record<number, AnchorState>;
  /** WHERE each note sits in the document — the panes' live anchor resolution,
   *  merged in reading order by `mergeSurfaces`. Optional: without it "Document
   *  order" still orders by each note's RECORDED anchor (a real document
   *  position, one paint stale), never by creation time. */
  readonly documentOrder?: DocumentOrder | null;
  readonly onReply: (
    parentId: number,
    draft: { body: string; body_format: string; mentions: number[] },
  ) => Promise<void>;
  readonly onStatus: (commentId: number, status: CommentStatus) => Promise<void>;
  readonly onFocusAnchor: (commentId: number) => void;
  readonly onCounterpart: (comment: WorkbenchComment) => void;
  readonly slug: string;
  readonly unitId: string;
  /** rich-comments §13: the comment whose card lifts (a doc highlight is hovered). */
  readonly linkedCommentId?: number | null;
  /** rich-comments §13.1: card→span hover — brighten the matching highlight. */
  readonly onHoverCard?: (commentId: number | null) => void;
  /** comment-visibility (§6A): viewer identity + change-scope handler. */
  readonly viewerId?: number;
  readonly viewerIsStaff?: boolean;
  readonly onVisibility?: (
    commentId: number,
    body: VisibilityChange,
  ) => Promise<void>;
  /** review-notes §3: persist an author body edit (author-only). */
  readonly onEdit?: (
    commentId: number,
    draft: { body: string; body_format: string },
  ) => Promise<void>;
  /** review-notes §4: toggle the viewer's emoji reaction on a comment/reply. */
  readonly onToggleReaction?: (
    commentId: number,
    emoji: string,
    add: boolean,
  ) => void;
  /** review-notes §2.3: deep-linked comment/reply id → auto-expand its thread. */
  readonly revealCommentId?: number | null;
  /** ticket / permalink deep-link: the ROOT comment id whose card gets the
   *  one-shot 3s slow-fade highlight (`comment-card--reveal`). Null = none. */
  readonly flashCommentId?: number | null;
  /** comment-delete: delete a comment/reply (AUTHOR-ONLY, enforced server-side).
   *  Absent ⇒ no delete affordance. The host owns the re-fetch. */
  readonly onDelete?: (commentId: number) => Promise<void>;
  // ── comment-version ribbon + switch (owner 2026-07-28) ─────────────────────
  //    Keyed BY RESOURCE ROLE, not flat: this gutter straddles a PAIRING (two
  //    panes), and a note anchored into the brief must be measured against the
  //    BRIEF's timeline, never the article's. Resolving per-comment through
  //    `home_resource` is the whole reason these are maps.
  /** Each pane's version ticks — the SAME lists the timelines render. */
  readonly versionTicksByRole?: Partial<Record<ResourceRole, readonly VersionTickLike[]>>;
  /** Each pane's currently-shown version (`null`/absent ⇒ the live head). */
  readonly displayedVersionByRole?: Partial<Record<ResourceRole, string | null>>;
  /** Switch a pane to `versionId`. MUST be the host's existing timeline switch
   *  (`ArticleWorkbench.onViewVersion`) — never a second implementation. */
  readonly onSwitchVersion?: (role: ResourceRole, versionId: string) => void;
}

export function CommentGutter({
  pairing,
  comments,
  members,
  anchorStates,
  documentOrder,
  onReply,
  onStatus,
  onFocusAnchor,
  onCounterpart,
  slug,
  unitId,
  linkedCommentId,
  onHoverCard,
  viewerId,
  viewerIsStaff,
  onVisibility,
  onEdit,
  onToggleReaction,
  revealCommentId,
  flashCommentId,
  onDelete,
  versionTicksByRole,
  displayedVersionByRole,
  onSwitchVersion,
}: CommentGutterProps) {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  // review-notes §1.4: GLOBAL per-reviewer sort preference (localStorage) — the
  // SAME key/control the DocumentViewer rail uses, so the choice is consistent.
  const [sort, setSort] = useState<ReviewSort>(() => loadReviewSort());
  useEffect(() => {
    saveReviewSort(sort);
  }, [sort]);

  const mine = useMemo(
    () => comments.filter((c) => commentBelongsInGutter(c, pairing)),
    [comments, pairing],
  );
  // buildThreads erases the WorkbenchComment subtype to Comment; keep a lookup so
  // the counterpart chip reads the routing columns off the real object.
  const routingById = useMemo(() => {
    const m = new Map<number, WorkbenchComment>();
    for (const c of mine) m.set(c.id, c);
    return m;
  }, [mine]);
  // W3: the server version-aware anchor state per comment (design §1.7). Drives
  // the comment card's exact/shifted/repaired("moved in v{n}")/orphaned badge.
  const rebaseStates = useMemo(() => {
    const m: Record<number, { state: import("../../api/articleUnits").RebaseState; seq: number }> = {};
    for (const c of mine) {
      if (c.anchor_state) m[c.id] = { state: c.anchor_state, seq: c.anchored_seq ?? 0 };
    }
    return m;
  }, [mine]);
  const roots = useMemo(
    () => sortRoots(buildThreads(mine), sort, documentOrder),
    [mine, sort, documentOrder],
  );
  // How many notes the "Pinned" filter would actually reveal, counted in ONE
  // pass over the same `roots` the rail lists — never a second `.filter()` whose
  // predicate could drift from the status predicate below. Roots only: a pinned
  // note is a thread, and the rail lists threads.
  const pinnedCount = useMemo(
    () => roots.reduce((n, t) => (t.comment.status === "pinned" ? n + 1 : n), 0),
    [roots],
  );
  const shown = roots.filter(
    (t) =>
      (typeFilter === "all" || t.comment.type === typeFilter) &&
      (statusFilter === "all" || t.comment.status === statusFilter),
  );
  // The residue of document order — see `UnanchoredHeading`. The rail computes
  // this the SAME way from the SAME helpers; the two columns cannot diverge.
  const unplaced =
    sort === "citation" ? unanchoredRoots(shown, documentOrder) : NO_UNANCHORED;
  const firstUnplaced = shown.findIndex((t) => unplaced.has(t.comment.id));

  const cb: ThreadCallbacks = {
    members,
    onReply,
    onStatus,
    onFocusAnchor,
    anchorStates,
    rebaseStates,
    slug,
    docId: unitId,
    linkedCommentId,
    onHoverCard,
    viewerId,
    viewerIsStaff,
    onVisibility,
    onEdit,
    onToggleReaction,
    revealCommentId,
    flashCommentId,
    onDelete,
  };

  return (
    <div className="wb-gutter__card" data-testid={`comment-gutter-${pairing}`} data-pairing={pairing}>
      <div
        className="rail__header"
        data-testid={`review-notes-header-${pairing}`}
        data-sort={sort}
      >
        <h3 className="rail__h">
          Review notes
          {roots.length > 0 ? <span className="rail__count">{roots.length}</span> : null}
        </h3>
        {roots.length > 0 ? (
          <ReviewNotesSort sort={sort} onSort={setSort} />
        ) : null}
      </div>

      {roots.length > 0 ? (
        <div className="rail__filters">
          <div className="rail__filter-row" role="radiogroup" aria-label="Filter by type">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="radio"
                aria-checked={typeFilter === f.value}
                className={`filter-pill${typeFilter === f.value ? " filter-pill--active" : ""}`}
                data-testid={`gutter-${pairing}-filter-type-${f.value}`}
                onClick={() => setTypeFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="rail__filter-row" role="radiogroup" aria-label="Filter by status">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="radio"
                aria-checked={statusFilter === f.value}
                className={`filter-pill${statusFilter === f.value ? " filter-pill--active" : ""}`}
                data-testid={`gutter-${pairing}-filter-status-${f.value}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
                {/* "Pinned 6". At ZERO the chip reads plain "Pinned" — a "0"
                    advertises an empty filter as if it were a result. */}
                {f.value === "pinned" && pinnedCount > 0 ? (
                  <span className="filter-pill__count">{pinnedCount}</span>
                ) : null}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {roots.length === 0 ? (
        <EmptyState
          icon={<MessagesSquare size={24} strokeWidth={1.5} />}
          title="No notes here"
          body="Comments addressing this pairing appear in this gutter."
        />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<MessagesSquare size={24} strokeWidth={1.5} />}
          title="No matching notes"
          body="No thread matches the current type / status filter."
        />
      ) : (
        <div className="thread-list" data-testid={`gutter-${pairing}-thread-list`}>
          {shown.map((t, i) => {
            const wc = routingById.get(t.comment.id);
            const label = wc ? counterpartLabel(wc) : null;
            // A gutter straddles a PAIRING, so "which timeline is this note
            // measured against?" is a PER-COMMENT question answered by the pane
            // its selector anchors into (`home_resource`). Building the version
            // slice here — rather than once for the whole gutter — is what stops
            // a brief-anchored note being compared to the article's version
            // numbers, which would mislabel its ribbon and switch the wrong pane.
            const role = wc?.home_resource;
            const cbForRoot: ThreadCallbacks = role
              ? {
                  ...cb,
                  versionTicks: versionTicksByRole?.[role] ?? [],
                  displayedVersionId: displayedVersionByRole?.[role] ?? null,
                  onSwitchVersion: onSwitchVersion
                    ? (versionId) => onSwitchVersion(role, versionId)
                    : undefined,
                }
              : cb;
            return (
              <Fragment key={t.comment.id}>
                {i === firstUnplaced ? (
                  <UnanchoredHeading count={shown.length - firstUnplaced} />
                ) : null}
                <div className="wb-gutter__thread">
                  <CommentThread node={t} depth={0} cb={cbForRoot} />
                  {wc && label ? (
                    <button
                      type="button"
                      className="wb-gutter__counterpart"
                      data-testid={`counterpart-chip-${t.comment.id}`}
                      onClick={() => onCounterpart(wc)}
                      title={`Scroll the ${label} pane to the linked section`}
                    >
                      <ArrowRight size={12} strokeWidth={1.5} />↔ {label}
                    </button>
                  ) : null}
                </div>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
